import { uuid } from '@sxm-core/utility-code/typescript/browser-common-cryptography';
import { initClickTrackFunctions } from './lib/init-click-track-functions';
import { DependenciesPayload } from '@sxm-de/dev-kits/web-sdk/types';
import { trackBusinessError } from '@sxm-de/dev-kits/web-sdk/utils';

type ClickTrackEventFn = (payload: {
    event: UIEvent;
    linkKey?: string;
}) => void;

type ClickTrackNameFn = (payload: { name: string; linkKey?: string }) => void;

export interface IAnalytics {
    initTransactionId: () => void;
    startTransaction: (payload: {
        flowInfo: {
            flowName: string;
            flowType?: string;
        };
    }) => void;
    componentImpression: (payload: { name: string }) => void;
    clickTracking: {
        trackSubmitWithEvent: ClickTrackEventFn;
        trackSubmitWithName: ClickTrackNameFn;
        trackExitWithEvent: ClickTrackEventFn;
        trackExitWithName: ClickTrackNameFn;
        trackModalWithEvent: ClickTrackEventFn;
        trackModalWithName: ClickTrackNameFn;
        trackRoutingWithEvent: ClickTrackEventFn;
        trackRoutingWithName: ClickTrackNameFn;
        trackUiWithEvent: ClickTrackEventFn;
        trackUiWithName: ClickTrackNameFn;
        trackPlayerWithEvent: ClickTrackEventFn;
        trackPlayerWithName: ClickTrackNameFn;
        trackDownloadWithEvent: ClickTrackEventFn;
        trackDownloadWithName: ClickTrackNameFn;
        trackChatWithEvent: ClickTrackEventFn;
        trackChatWithName: ClickTrackNameFn;
        trackRefreshWithEvent: ClickTrackEventFn;
        trackRefreshWithName: ClickTrackNameFn;
        trackLoginWithEvent: ClickTrackEventFn;
        trackLoginWithName: ClickTrackNameFn;
    };
    clientSideValidationErrors: (payload: { errors: string[] }) => void;
    businessError: (payload: { code: string; message: string }) => void;
}

export function analytics({ appEventSdk }: DependenciesPayload): IAnalytics {
    const {
        clickTrackSubmit,
        clickTrackExit,
        clickTrackModal,
        clickTrackRouting,
        clickTrackUi,
        clickTrackPlayer,
        clickTrackDownload,
        clickTrackChat,
        clickTrackRefresh,
        clickTrackLogin,
    } = initClickTrackFunctions(appEventSdk);

    return {
        initTransactionId: () => {
            appEventSdk.trackEvent({
                event: 'transaction-id-update',
                transactionInfo: { transactionId: `OAC-${uuid()}` },
            });
        },
        startTransaction: ({
            flowInfo,
        }: {
            flowInfo: {
                flowName: string;
                flowType?: string;
            };
        }) => {
            appEventSdk.trackEvent({
                event: 'flow-started',
                flowInfo,
            });
        },
        componentImpression: (payload: { name: string }) => {
            appEventSdk.trackEvent({
                event: 'component-loaded',
                componentInfo: { componentName: payload.name },
            });
        },
        clickTracking: {
            trackSubmitWithEvent: clickTrackSubmit,
            trackSubmitWithName: clickTrackSubmit,
            trackExitWithEvent: clickTrackExit,
            trackExitWithName: clickTrackExit,
            trackModalWithEvent: clickTrackModal,
            trackModalWithName: clickTrackModal,
            trackRoutingWithEvent: clickTrackRouting,
            trackRoutingWithName: clickTrackRouting,
            trackUiWithEvent: clickTrackUi,
            trackUiWithName: clickTrackUi,
            trackPlayerWithEvent: clickTrackPlayer,
            trackPlayerWithName: clickTrackPlayer,
            trackDownloadWithEvent: clickTrackDownload,
            trackDownloadWithName: clickTrackDownload,
            trackChatWithEvent: clickTrackChat,
            trackChatWithName: clickTrackChat,
            trackRefreshWithEvent: clickTrackRefresh,
            trackRefreshWithName: clickTrackRefresh,
            trackLoginWithEvent: clickTrackLogin,
            trackLoginWithName: clickTrackLogin,
        },
        clientSideValidationErrors: (payload: { errors: string[] }) => {
            appEventSdk.trackEvent({ event: 'user-error', payload });
        },
        businessError: (payload) => {
            trackBusinessError(appEventSdk, payload);
        },
    };
}

export * from './lib/track-user-email';
